import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from '@angular/fire/firestore';
import type { Customer, CustomerPage } from './customer';
import type { CustomerOrder } from './customer-sort';

export const CUSTOMERS_PAGE_SIZE = 25;

// Customers are read-only in the app, the backend owns every write
const customerConverter: FirestoreDataConverter<Customer> = {
  toFirestore: () => {
    throw new Error('Customers are written by the backend only');
  },
  fromFirestore: (snapshot) => ({ id: snapshot.id, ...snapshot.data() }) as Customer,
};

/** Firestore access for companies/{companyId}/customers. */
@Injectable({ providedIn: 'root' })
export class CustomerApi {
  readonly #firestore = inject(Firestore);

  #collection(companyId: string) {
    return collection(this.#firestore, 'companies', companyId, 'customers').withConverter(customerConverter);
  }

  /**
   * One page of customers in the given order. Oldest last order first leaves out customers without
   * printed orders, who would otherwise fill the first pages (null sorts first ascending).
   * One-time read rather than a listener, so rows don't shift while paging. One extra document
   * is requested to know whether a next page exists without a count query.
   */
  async getPage(
    companyId: string,
    { sort, direction }: CustomerOrder,
    after: QueryDocumentSnapshot<Customer> | null,
  ): Promise<CustomerPage> {
    const { docs } = await getDocs(
      query(
        this.#collection(companyId),
        ...(sort === 'lastOrderAt' && direction === 'asc' ? [where('lastOrderAt', '!=', null)] : []),
        orderBy(sort, direction),
        ...(after ? [startAfter(after)] : []),
        limit(CUSTOMERS_PAGE_SIZE + 1),
      ),
    );
    const pageDocs = docs.slice(0, CUSTOMERS_PAGE_SIZE);
    return {
      customers: pageDocs.map((snapshot) => snapshot.data()),
      cursor: pageDocs.at(-1) ?? null,
      hasMore: docs.length > CUSTOMERS_PAGE_SIZE,
    };
  }

  /** One customer, undefined when the document does not exist. */
  async get(companyId: string, customerId: string): Promise<Customer | undefined> {
    const snapshot = await getDoc(doc(this.#collection(companyId), customerId));
    return snapshot.data();
  }

  /** Number of customers of the company, billed as one read per 1000 documents. */
  async count(companyId: string): Promise<number> {
    const snapshot = await getCountFromServer(this.#collection(companyId));
    return snapshot.data().count;
  }
}
